import React, { useState, memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "react-bootstrap";
import styled, { css } from "styled-components";
import ballIcon from "../../images/Ball_Icon.png";
import handIcon from "../../images/Bowler_Hand_Icon.png";
import Image from "../ui/Image";
import BowlerInfoModal from "../ui/BowlerInfoModal";
import ConfirmDeleteBowler from "../ui/ConfirmDeleteBowler";
import { MoreOutlined } from "@ant-design/icons";

const imageStyle = {
  width: "64px",
  height: "64px",
  borderRadius: "32px",
};

const footerStyle = {
  background: "#fafbfd",
  borderTop: "1px solid #eef0f5",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  height: "50.26px",
};

const footerTextStyle = {
  font: "normal normal 600 12px/15px Poppins",
  letterSpacing: "0px",
  color: "#00000066",
  opacity: "1",
  background: "transparent",
  border: "none",
};

const iconHandStyle = {
  width: "23px",
  height: "26px",
  marginRight: "5px",
};
const iconBallStyle = {
  width: "23px",
  height: "23px",
  marginRight: "5px",
};

const CardContainer = styled(Card)`
  border: 1px solid #ececef;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(16, 24, 64, 0.06);
  transition: box-shadow 0.18s ease, transform 0.18s ease,
    border-color 0.18s ease;

  &:hover {
    box-shadow: 0 8px 22px rgba(16, 24, 64, 0.12);
    border-color: #d8dbe8;
    transform: translateY(-2px);
  }
`;

const cardBodyPadding = {
  padding: "0",
  position: "relative",
};

const Button = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  border-radius: 50%;
  background: rgba(0, 20, 123, 0.45);
  color: #ffffff;
  width: 30px;
  height: 30px;
  border: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  transition: background 0.18s ease;

  &:hover {
    background: #00147b;
  }
`;

const LabelContainer = styled.span`
  font: Poppins;
  opacity: 1;
  margin-top: 3px;
  font-size: ${(props) => (props.size > 20 ? "14px" : "22px;")};
  font-weight: 700;
  color: #000000cc;
  opacity: 1;
  margin-top: 3px;
  text-align: left;
  cursor: pointer;

  ${(props) =>
    props.name &&
    css`
      font-size: ${(props) => (props.size > 20 ? "10px" : "16px;")};
      margin-top: 3px;
    `}
`;

const LabelDate = styled.div`
  font: Poppins;
  opacity: 1;
  margin-top: 3px;
  font-size: 8px;
  font-weight: 700;
  color: #00000066;
  opacity: 1;
  margin-top: 3px;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-evenly;
  self-align: flex-end;
  position: absolute;
  bottom: 2px;
  left: 0;
  width: 100%;
  flex-direction: row;
`;

const ContainerMod = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.view === "one" ? "row" : "column")};
  justify-content: space-between;
  padding-top: 0.25em;
  padding-bottom: 0.25em;
  margin-bottom: 2vh;
  width: 100%;
  height: 100%;

  &[class*="column1"] {
    padding-left: 0.25em;
    padding-right: 0.25em;
  }

  &[class*="column0"] {
    padding-left: 0.5em;
  }

  &[class*="column2"] {
    padding-right: 0.5em;
  }
`;

const ImageDiv = styled.div`
  border-radius: 50%;
  width: 65px;
  height: unset;
  aspect-ratio: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 3px rgba(0, 24, 126, 0.06);

  img.image-loaded {
    aspect-ratio: 1;
  }
`;

const MarginCol = styled.div`
  width: 70%;
  font: 700 14px / 1 Poppins;
  color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const CardImage = ({ src, alt }) => (
  <div
    style={{
      width: "30%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <ImageDiv>
      <Image
        style={{ width: "100%", height: "unset", borderRadius: "50%" }}
        src={src}
        alt={alt}
      />
    </ImageDiv>
  </div>
);

const generatePlaceholderImage = (name, lastName) => {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#00187E";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "bold 28px Poppins";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const initials = `${name.charAt(0).toUpperCase()}${lastName
    .charAt(0)
    .toUpperCase()}`;

  ctx.fillText(initials, canvas.width / 2, canvas.height / 2);

  return canvas.toDataURL("image/png");
};

export const BowlerCard = memo(
  ({
    name = "First Name",
    lastName = "Last Name",
    email = "Email",
    phone = "Phone",
    language = "",
    created_at,
    updated_at,
    specSheets = 0,
    balls = 0,
    orderBy,
    image,
    id,
    onDelete,
    className,
    isReadOnly,
    current_woocommerce_id,
    woocommerceCheckUserExists,
    pluginSubscriptions,
  }) => {
    const [showBowlerInfoModal, setBowlerInfoModal] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleUserNavigate = () => {
      navigate("/bowler/" + id.toString());
    };

    const handleArsenalNavigate = () => {
      navigate("/arsenal/" + id.toString());
    };

    const showPlaceholder = image == null;
    const defaultImage = useMemo(
      () => (showPlaceholder ? generatePlaceholderImage(name, lastName) : null),
      [showPlaceholder, name, lastName]
    );

    return (
      <ContainerMod className={className}>
        <CardContainer>
          <Card.Body style={cardBodyPadding} onClick={handleUserNavigate}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                height: "100%",
              }}
            >
              <Card.Img
                style={imageStyle}
                src={showPlaceholder ? defaultImage : image}
                fluid={"true"}
                alt={""}
                as={CardImage}
              />
              <MarginCol>
                <LabelContainer
                  size={lastName.length}
                  style={{ opacity: "0.8" }}
                >
                  {lastName.length >= 21
                    ? lastName.substring(0, 19).toUpperCase() + "..."
                    : lastName.toUpperCase()}
                </LabelContainer>
                <LabelContainer
                  name
                  size={name.length}
                  style={{ opacity: "0.8" }}
                >
                  {name.length >= 21
                    ? name.substring(0, 19).toUpperCase() + "..."
                    : name.toUpperCase()}
                </LabelContainer>

                <LabelContainer
                  style={{ fontSize: "12px", opacity: "0.5" }}
                  size={phone.length}
                >
                  {phone.length >= 21 ? phone.substring(0, 19) + "..." : phone}
                </LabelContainer>

                <LabelContainer
                  style={{ fontSize: "12px", opacity: "0.5" }}
                  size={email.length}
                >
                  {email.length >= 31 ? email.substring(0, 29) + "..." : email}
                </LabelContainer>

                {pluginSubscriptions.indexOf("BOWLER_PLUS") >= 0 && (
                  <LabelContainer
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                    size={language.length}
                  >
                    {language.length >= 31
                      ? language.substring(0, 29) + "..."
                      : language}
                  </LabelContainer>
                )}

                {/*Email/telephone go here when filter by setting is set */}
                {/* <LabelDate>
                  <div>
                    C: {moment(created_at).format("MM/DD/YYYY hh:mm:ss")}
                  </div>
                  <div>
                    U: {moment(updated_at).format("MM/DD/YYYY hh:mm:ss")}
                  </div>
                </LabelDate> */}
              </MarginCol>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setBowlerInfoModal(true);
                }}
              >
                <MoreOutlined style={{ fontSize: 22 }} />
              </Button>
            </div>
          </Card.Body>
          <Card.Footer style={footerStyle}>
            <div className="row">
              <button
                className="text-muted col-7 col-md-7 col-lg-7"
                style={footerTextStyle}
                onClick={handleUserNavigate}
              >
                <img style={iconHandStyle} src={handIcon} alt="hand logo" />
                {specSheets} {t("form.label.spec_sheets")}
              </button>
              <button
                className="text-muted col-5 col-md-5 col-lg-5"
                style={footerTextStyle}
                onClick={handleArsenalNavigate}
              >
                <img style={iconBallStyle} src={ballIcon} alt="ball logo" />
                {balls} {t("form.label.balls")}
              </button>
              <BowlerInfoModal
                show={showBowlerInfoModal}
                onHide={() => {
                  setBowlerInfoModal(false);
                }}
                onEdit={() => {
                  handleUserNavigate();
                  setBowlerInfoModal(false);
                }}
                onDelete={() => {
                  setShowConfirmDelete(true);
                  setBowlerInfoModal(false);
                }}
                isReadOnly={isReadOnly}
                created_at={created_at}
                updated_at={updated_at}
              />
              <ConfirmDeleteBowler
                show={showConfirmDelete}
                onHide={() => {
                  setShowConfirmDelete(false);
                }}
                onConfirmClick={() => {
                  onDelete && onDelete(id);
                  setShowConfirmDelete(false);
                }}
              />
            </div>
          </Card.Footer>
        </CardContainer>
      </ContainerMod>
    );
  }
);
